/**
 * CascadePanel — inline mini network diagram showing how a policy apply
 * cascaded through downstream successors.  Renders as pure SVG so we
 * don't pull in react-flow / d3-force.
 *
 * Props:
 *   open, onClose        — visibility
 *   payload              — { nodes: [], edges: [], movedCount, topShown }
 *   loading              — bool
 *   onUndo               — () => void (Undo Policy chip)
 *   canUndo              — bool
 */
import { X, GitBranch, Undo2, Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';

const fmtDelta = (n) => (n > 0 ? `+${n}d` : n < 0 ? `${n}d` : '0d');

export default function CascadePanel({ open, onClose, payload, loading, onUndo, canUndo }) {
  if (!open) return null;
  const nodes = payload?.nodes || [];
  const edges = payload?.edges || [];

  // Simple manual layout: left column = moved (predecessors), right = successors
  const moved = nodes.filter((n) => n.role === 'moved');
  const succs = nodes.filter((n) => n.role === 'successor');

  const width = 720;
  const rowH = 42;
  const height = Math.max(160, Math.max(moved.length, succs.length) * rowH + 40);
  const colL = 20;
  const colR = width - 260;

  const posL = Object.fromEntries(moved.map((n, i) => [n.id, { x: colL, y: 30 + i * rowH }]));
  const posR = Object.fromEntries(succs.map((n, i) => [n.id, { x: colR, y: 30 + i * rowH }]));

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.55)' }}
         onClick={onClose} data-testid="cascade-panel">
      <div className="relative rounded-lg border shadow-2xl max-h-[85vh] overflow-hidden flex flex-col"
           style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)', width: width + 40 }}
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-3 py-2 border-b"
             style={{ borderColor: 'var(--border-color)', background: 'var(--card-bg-alt)' }}>
          <div className="flex items-center gap-2">
            <GitBranch className="w-4 h-4 text-amber-400" />
            <div>
              <div className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Predecessor Cascade</div>
              <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                {loading ? 'Building cascade…' : `${payload?.movedCount || 0} tasks moved · top ${payload?.topShown || 0} shown with direct successors`}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canUndo && (
              <Button size="sm" variant="outline" onClick={onUndo}
                      className="h-7 px-2 text-[11px] border-red-500/40 text-red-400 hover:bg-red-500/10"
                      data-testid="cascade-undo-btn">
                <Undo2 className="w-3.5 h-3.5 mr-1" /> Undo policy
              </Button>
            )}
            <button onClick={onClose} className="p-1 rounded hover:bg-white/5" data-testid="cascade-close"
                    style={{ color: 'var(--text-muted)' }}>
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-3">
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--text-muted)' }} />
            </div>
          ) : nodes.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 gap-2 text-center">
              <AlertTriangle className="w-6 h-6 text-amber-400" />
              <div className="text-xs" style={{ color: 'var(--text-primary)' }}>No cascade to display</div>
              <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Either no tasks moved or their successors didn't shift.</div>
            </div>
          ) : (
            <svg width={width} height={height} data-testid="cascade-svg">
              <defs>
                <marker id="arrowhead" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto">
                  <path d="M0,0 L8,4 L0,8 z" fill="#94a3b8" />
                </marker>
              </defs>
              {edges.map((e, i) => {
                const p = posL[e.from]; const q = posR[e.to];
                if (!p || !q) return null;
                const y1 = p.y + rowH / 2, y2 = q.y + rowH / 2;
                const mx = (p.x + 220 + q.x) / 2;
                const color = e.dFinish > 0 ? '#ef4444' : e.dFinish < 0 ? '#22c55e' : '#94a3b8';
                return (
                  <g key={i}>
                    <path d={`M${p.x + 220},${y1} C${mx},${y1} ${mx},${y2} ${q.x},${y2}`}
                          fill="none" stroke={color} strokeWidth="1.5" markerEnd="url(#arrowhead)" opacity="0.7" />
                    <text x={(p.x + 220 + q.x) / 2} y={(y1 + y2) / 2 - 4}
                          textAnchor="middle" fontSize="9" fill={color} fontWeight="600">
                      {fmtDelta(e.dFinish)} · {e.type}
                    </text>
                  </g>
                );
              })}
              {moved.map((n) => (
                <g key={n.id} data-testid={`cascade-moved-${n.id}`}>
                  <rect x={posL[n.id].x} y={posL[n.id].y} width="220" height={rowH - 6}
                        rx="4" fill={n.is_critical ? '#ef444422' : 'var(--card-bg-alt)'}
                        stroke={n.is_critical ? '#ef4444' : 'var(--border-color)'} strokeWidth="1" />
                  <text x={posL[n.id].x + 8} y={posL[n.id].y + 14}
                        fontSize="10" fill="var(--text-primary)" fontWeight="600">
                    {(n.name || n.id || '?').slice(0, 28)}
                  </text>
                  <text x={posL[n.id].x + 8} y={posL[n.id].y + 28}
                        fontSize="9" fill={n.dFinish > 0 ? '#ef4444' : '#22c55e'} fontWeight="600">
                    {n.wbs} · Δfinish {fmtDelta(n.dFinish)} · {n.policy || 'MOVED'}
                  </text>
                </g>
              ))}
              {succs.map((n) => (
                <g key={n.id} data-testid={`cascade-succ-${n.id}`}>
                  <rect x={posR[n.id].x} y={posR[n.id].y} width="240" height={rowH - 6}
                        rx="4" fill={n.is_critical ? '#ef444422' : 'var(--card-bg-alt)'}
                        stroke={n.is_critical ? '#ef4444' : 'var(--border-color)'} strokeWidth="1" />
                  <text x={posR[n.id].x + 8} y={posR[n.id].y + 14}
                        fontSize="10" fill="var(--text-primary)" fontWeight="600">
                    {(n.name || n.id || '?').slice(0, 30)}
                  </text>
                  <text x={posR[n.id].x + 8} y={posR[n.id].y + 28}
                        fontSize="9" fill={n.dFinish > 0 ? '#ef4444' : n.dFinish < 0 ? '#22c55e' : 'var(--text-muted)'} fontWeight="600">
                    {n.wbs} · re-flowed {fmtDelta(n.dFinish)}
                  </text>
                </g>
              ))}
            </svg>
          )}
        </div>

        <div className="px-3 py-1.5 border-t text-[9px] flex items-center gap-3"
             style={{ borderColor: 'var(--border-color)', background: 'var(--card-bg-alt)', color: 'var(--text-muted)' }}>
          <span><span className="inline-block w-3 h-3 rounded mr-1" style={{ background: '#ef4444' }} /> Pushed later</span>
          <span><span className="inline-block w-3 h-3 rounded mr-1" style={{ background: '#22c55e' }} /> Pulled earlier</span>
          <span><span className="inline-block w-3 h-3 rounded mr-1 border border-red-500" /> Critical path</span>
        </div>
      </div>
    </div>
  );
}
